import i18n, { Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import AppLangs from '../AppLangs';
import rootContainer from './rootContainer';
import { appLanguages } from '../features/lang/hooks/useAppLangs';
import detector from 'i18next-browser-languagedetector';
import * as XLSX from 'xlsx';
import _ from 'lodash';
import { TAppLangItem } from './i18nTypes';

const resources = {} as Resource;

const checkDoublons: string[] = [];

declare module 'i18next' {
	interface CustomTypeOptions {
		returnNull: false;
	}
}

// `appLanguages.code_iso2` is currently 'fr' or 'en' — kept narrow so the
// `item[code]` and `plurals[key][code]` accesses below stay type-checked.
type LangCode = 'fr' | 'en';

AppLangs.forEach((appsLang) => {
	Object.values(appsLang).forEach((item: TAppLangItem) => {
		if (checkDoublons.some((x) => x === item.tag)) {
			rootContainer?.render(<>{`/!\\ Doublons sur le tag ${item.tag} dans la configuration des langues /!\\`}</>);
			throw new Error(`i18n config lang duplicate id tag ${item.tag}`);
		} else {
			checkDoublons.push(item.tag);
		}
		appLanguages
			.filter((x) => x.display)
			.forEach((lang) => {
				if (resources[lang.code_iso2] === undefined) {
					resources[lang.code_iso2] = {};
				}
				if (resources[lang.code_iso2].translation === undefined) {
					resources[lang.code_iso2].translation = {};
				}
				const translation = resources[lang.code_iso2].translation as Record<string, string | undefined>;
				const code = lang.code_iso2 as LangCode;
				translation[item.tag] = item[code];

				if (item.plurals !== undefined) {
					(Object.keys(item.plurals) as Array<keyof typeof item.plurals>).forEach((pluralKey) => {
						translation[item.tag + '_' + pluralKey] = item.plurals?.[pluralKey]?.[code];
					});
				}
			});
	});
});

const bExportLangToExcel = false;
if (bExportLangToExcel) {
	// Export langs to excel
	const fileName = `Translations.xlsx`;
	const headers = ['Tag', 'Plurals', 'FR', 'EN'];

	type JsonLangRow = { tag: string; fr?: string; en?: string };
	const jsonLangs: JsonLangRow[] = [];

	AppLangs.forEach((appsLang) => {
		Object.values(appsLang).forEach((item: TAppLangItem) => {
			jsonLangs.push({ tag: item.tag });

			appLanguages
				.filter((x) => x.display)
				.forEach((lang) => {
					const code = lang.code_iso2 as LangCode;
					const jsonLang = jsonLangs.find((x) => x.tag === item.tag);
					if (jsonLang) jsonLang[code] = item[code];

					if (item.plurals) {
						(Object.keys(item.plurals) as Array<keyof typeof item.plurals>).forEach((pluralKey) => {
							const pluralTag = item.tag + '_' + pluralKey;
							const jsonLangPlural = jsonLangs.find((x) => x.tag === pluralTag);

							if (jsonLangPlural) {
								jsonLangPlural[code] = item.plurals?.[pluralKey]?.[code];
							} else {
								jsonLangs.push({ tag: pluralTag });
							}
						});
					}
				});
		});
	});

	const worksheet = XLSX.utils.json_to_sheet(jsonLangs);
	const workbook = XLSX.utils.book_new();
	XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A1' });
	XLSX.utils.book_append_sheet(workbook, worksheet, 'Portfolio');
	worksheet['!cols'] = _.keys(jsonLangs[0]).map((field, index) => {
		const header = headers[index] ?? '';
		return {
			wch: Math.min(
				Math.max(
					header.length,
					Math.max(
						...jsonLangs.map((obj) => {
							const value = obj[field as keyof JsonLangRow];
							return value ? value.toString().length : 0;
						}),
					),
				),
				50,
			),
		};
	});
	XLSX.writeFileXLSX(workbook, fileName);
}

i18n.use(detector)
	.use(initReactI18next) // passes i18n down to react-i18next
	.init({
		resources,
		keySeparator: false,
		fallbackLng: appLanguages.find((x) => x.default)?.code_iso2,
		lng: 'en',
		react: {
			defaultTransParent: 'div',
			useSuspense: false,
		},
		interpolation: {
			escapeValue: false, // react already safes from xss
		},
		returnNull: false,
	});

export default i18n;
